import { useEffect, useState } from "react";
import type { User } from "oidc-client-ts";
import { fetchAuthConfig, login, logout, subscribe, getCurrentUser } from "../lib/auth";

interface Props {
  variant?: "header" | "inline";
}

export function AuthButton({ variant = "header" }: Props): JSX.Element | null {
  const [enabled, setEnabled] = useState(false);
  const [user, setUser] = useState<User | null>(getCurrentUser());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    void fetchAuthConfig().then((c) => setEnabled(c.enabled));
    const off = subscribe(setUser);
    return off;
  }, []);

  if (!enabled) return null;

  if (user) {
    const name = user.profile.name || user.profile.preferred_username || user.profile.email || "Account";
    return (
      <div className={variant === "header" ? "flex items-center gap-2" : "flex items-center gap-3"}>
        <span className="text-xs text-ink-300 truncate max-w-[160px]">{name}</span>
        <button
          onClick={async () => {
            setLoading(true);
            try {
              await logout();
            } finally {
              setLoading(false);
            }
          }}
          disabled={loading}
          className="text-xs text-ink-400 hover:text-white border border-ink-600 rounded-lg px-3 py-1.5 disabled:opacity-50"
        >
          Sign out
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={async () => {
        setLoading(true);
        try {
          await login();
        } finally {
          setLoading(false);
        }
      }}
      disabled={loading}
      className={
        variant === "header"
          ? "text-xs font-medium bg-lime-500 hover:bg-lime-400 text-ink-900 rounded-lg px-3 py-1.5 disabled:opacity-50"
          : "w-full rounded-lg bg-lime-500 hover:bg-lime-400 disabled:opacity-50 text-ink-900 font-medium px-4 py-2 text-sm"
      }
    >
      {loading ? "Redirecting…" : "Sign in"}
    </button>
  );
}
